import React, { CSSProperties, SVGProps, useCallback } from 'react';
import classNames from 'classnames';
import { SvgIcon } from 'ui/SvgIcon';
import LoaderIcon from 'ui/Icons/tabler/loader.component.svg';
import PencilIcon from 'ui/Icons/tabler/pencil.component.svg';
import FileIcon from 'ui/Icons/tabler/file.component.svg';
import ArrowRightIcon from 'ui/Icons/tabler/arrow-right.component.svg';

export { CircleIconButtonWithTooltip } from './WithTooltip';

export interface IIconProps {
  background?: string;
  border?: string;
  fill?: string;
  stroke?: string;
  icon?: string;
  width?: number;
  height?: number;
  className?: string;
  onClick?: (e?: React.MouseEvent<Element>) => void;
}

export interface ICircleIconButtonProps {
  type: 'primary' | 'secondary';
  /** @deprecated Use IconComponent instead. This prop will be removed in a future version. */
  iconClass?: string;
  iconSrc?: string;
  /** SVG icon component to render (e.g., from ui/Icons/tabler/) */
  IconComponent?: React.FC<SVGProps<SVGSVGElement>>;
  /** Size of the icon (only used with IconComponent). Defaults to 'default' */
  iconSize?: 'xs' | 'sm' | 'default' | 'lg' | '2x' | '3x';
  style?: CSSProperties;
  onClick?: (e) => void;
  className?: string;
  disabled?: boolean;
}

export const CircleIconButton = (props: ICircleIconButtonProps) => {
  const { iconSize = 'default', IconComponent, iconClass, iconSrc } = props;

  let buttonClassName = classNames(
    `default:m-0 default:rounded-full default:border default:border-solid font-pt-sans flex justify-center items-center default:outline-none default:cursor-pointer`,
    {
      'bg-brown-80 border-brown-80 hover:bg-brown-120': props.type === 'primary' && !props.disabled,
      'bg-white border-brown-80 hover:bg-brown-20': props.type === 'secondary' && !props.disabled,
      'bg-gray-80 border-gray-80': props.type === 'primary' && props.disabled,
      'bg-white border-gray-80': props.type === 'secondary' && props.disabled,
    }
  );

  // Determine button size based on iconSize or iconClass (for backwards compatibility)
  if (IconComponent) {
    // New IconComponent prop - use iconSize
    if (iconSize === 'xs' || iconSize === 'sm') {
      buttonClassName = `${buttonClassName} w-6 h-6`;
    } else if (iconSize === 'lg') {
      buttonClassName = `${buttonClassName} w-10 h-10`;
    } else if (iconSize === '2x') {
      buttonClassName = `${buttonClassName} w-12 h-12`;
    } else if (iconSize === '3x') {
      buttonClassName = `${buttonClassName} w-14 h-14`;
    } else {
      buttonClassName = `${buttonClassName} w-30px h-30px`;
    }
  } else if (iconClass) {
    // Legacy iconClass prop - check for FA size classes
    if (iconClass.includes('fa-xs') || iconClass.includes('fa-sm')) {
      buttonClassName = `${buttonClassName} w-6 h-6`;
    } else if (iconClass.includes('fa-lg')) {
      buttonClassName = `${buttonClassName} w-10 h-10`;
    } else if (iconClass.includes('fa-2x')) {
      buttonClassName = `${buttonClassName} w-12 h-12`;
    } else if (iconClass.includes('fa-3x')) {
      buttonClassName = `${buttonClassName} w-14 h-14`;
    } else {
      buttonClassName = `${buttonClassName} w-30px h-30px`;
    }
  } else {
    buttonClassName = `${buttonClassName} w-30px h-30px`;
  }

  if (props.className != undefined) {
    buttonClassName = `${buttonClassName} ${props.className}`;
  }

  // List of fill-based icons (Font Awesome icons that use fill instead of stroke)
  const fillBasedIcons = [PencilIcon, FileIcon, ArrowRightIcon];
  const isFillBasedIcon = IconComponent && fillBasedIcons.includes(IconComponent);

  // Determine stroke color for stroke-based icons
  const getStrokeColor = () => {
    if (props.type === 'primary') {
      return 'stroke-white';
    } else if (props.type === 'secondary' && !props.disabled) {
      return 'stroke-brown-80';
    } else {
      return 'stroke-gray-80';
    }
  };

  // Determine fill color for fill-based icons
  const getFillColor = () => {
    if (props.type === 'primary') {
      return 'fill-white';
    } else if (props.type === 'secondary' && !props.disabled) {
      return 'fill-brown-80';
    } else {
      return 'fill-gray-80';
    }
  };

  // Determine icon size (width and height) for new IconComponent
  const getIconSize = () => {
    if (iconSize === 'xs') return 'w-3 h-3';
    if (iconSize === 'sm') return 'w-3.5 h-3.5';
    if (iconSize === 'lg') return 'w-5 h-5';
    if (iconSize === '2x') return 'w-6 h-6';
    if (iconSize === '3x') return 'w-7 h-7';
    return 'w-4 h-4'; // default (16px - matches FA icon visual weight better)
  };

  const handleClick = useCallback(
    e => {
      !props.disabled && props.onClick && props.onClick(e);
    },
    [props]
  );

  // Render new IconComponent if provided
  const renderIcon = () => {
    if (iconSrc) {
      const legacyIconClass = classNames(`text-sm ${iconClass}`, {
        'block text-white': props.type === 'primary',
        'block text-brown-80': props.type === 'secondary' && !props.disabled,
        'block text-gray-80': props.type === 'secondary' && props.disabled,
      });
      return <img className={legacyIconClass} src={iconSrc} />;
    }

    if (IconComponent) {
      const isLoader = IconComponent === LoaderIcon;

      // Use fill for fill-based icons, stroke for stroke-based icons
      const colorClasses = isFillBasedIcon
        ? getFillColor()
        : `fill-transparent ${getStrokeColor()} stroke-2`;

      return (
        <SvgIcon
          IconComponent={IconComponent}
          className={`${getIconSize()} ${colorClasses} ${isLoader ? 'animate-spin' : ''}`}
        />
      );
    }

    // Legacy iconClass fallback
    const legacyIconClass = classNames(`text-sm ${iconClass}`, {
      'block text-white': props.type === 'primary',
      'block text-brown-80': props.type === 'secondary' && !props.disabled,
      'block text-gray-80': props.type === 'secondary' && props.disabled,
    });
    return <i className={legacyIconClass} />;
  };

  return (
    <span
      className={classNames(buttonClassName, {
        'cursor-pointer': !props.disabled,
        'cursor-default opacity-60': props.disabled,
      })}
      style={props.style ?? {}}
      onClick={e => handleClick(e)}
    >
      {renderIcon()}
    </span>
  );
};
